/**
 * What a filter can ASK of a column.
 *
 * A filter used to be one thing: a list of keys for one dimension under an
 * implied "is any of". That is the right question for Customer and Status and
 * the wrong one for everything else — a number filtered as a lookup can only be
 * asked "is it exactly one of these", which is never what somebody wants to
 * know about Hours.
 *
 * So a filter is a SENTENCE — column, operator, value — and the operators a
 * column offers follow from its {@link FilterValueType}. Nothing is declared
 * per column beyond that type: the operator list, the default operator, the
 * value editor and the SQL cast are all derived, so a new filterable dimension
 * needs no UI and no compiler change.
 *
 * Grouping is ONE LEVEL deep. A group of groups cannot be written on a filter
 * bar, and the whole design rests on an applied filter being readable there
 * rather than collapsed into "3 rules". See docs/reporting/filter-operators.md.
 */
import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import { z } from 'zod';

/**
 * What kind of thing a column holds.
 *
 * `lookup` is the default because it is what every dimension was before this
 * existed: a key with a label, filtered by picking members. A model that says
 * nothing therefore behaves exactly as it did.
 */
export const FILTER_VALUE_TYPES = [
  'lookup',
  'text',
  'number',
  'date',
  'datetime',
  'boolean',
] as const;
export type FilterValueType = (typeof FILTER_VALUE_TYPES)[number];

export const FILTER_OPS = [
  // membership
  'in',
  'not_in',
  // text
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  // equality, for a single value of any ordered type
  'eq',
  'neq',
  // ordered
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  // dates
  'before',
  'after',
  'within',
  // any type
  'blank',
  'not_blank',
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

/**
 * The shape of the value an operator takes.
 *
 * A TAGGED union rather than a bare `string | number | [number, number]`,
 * because the tag is what lets the compiler switch on the value it was given
 * instead of inferring the shape from the operator and hoping the two agree.
 * `none` is not represented at all: a blank check carries no value, and an
 * absent field says that better than an empty one.
 */
export const FILTER_ARITIES = [
  'none',
  'list',
  'text',
  'number',
  'numberRange',
  'date',
  'dateRange',
  'macro',
  'boolean',
] as const;
export type FilterArity = (typeof FILTER_ARITIES)[number];

/** A date, or a date with a time. Seconds optional; no zone — see below. */
const isoInstant = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/;

/**
 * Instants carry no zone on purpose.
 *
 * "Approved after Aug 31, 17:00" is a question asked in the TENANT's zone, and
 * the compiler is where that zone is known. A value carrying its own offset
 * would mean two people sharing one saved report could see different rows
 * depending on where they were when they saved it.
 */
export const filterValueSchema = z.union([
  /** Member KEYS, for the operators that pick from a list. */
  z.object({ kind: z.literal('list'), values: z.array(z.string().max(400)).min(1).max(500) }),
  /**
   * ONE string, for the string operators.
   *
   * Not a list of one. "Description starts with phone, holiday" was two
   * questions in a control that read as one, and the answer to "which rows
   * start with holiday" is not "the ones that start with phone as well" —
   * somebody reading the chip had to know the terms were ORed to know what
   * they were looking at. One value per rule; two questions are two rules,
   * and a group is how you say "either".
   */
  z.object({ kind: z.literal('text'), value: z.string().min(1).max(400) }),
  z.object({ kind: z.literal('number'), value: z.number().finite() }),
  z.object({
    kind: z.literal('numberRange'),
    min: z.number().finite().nullable(),
    max: z.number().finite().nullable(),
  }),
  z.object({ kind: z.literal('date'), value: z.string().regex(isoInstant) }),
  z.object({
    kind: z.literal('dateRange'),
    from: z.string().regex(isoInstant).nullable(),
    to: z.string().regex(isoInstant).nullable(),
  }),
  z.object({ kind: z.literal('macro'), preset: z.enum(DATE_RANGE_PRESETS) }),
  z.object({
    kind: z.literal('boolean'),
    /**
     * Spelled any of the ways a boolean gets written.
     *
     * The control sends `true`, but a filter can also arrive from an API
     * caller or a pasted spec, and `Y`, `t`, `Yes` and `1` all plainly mean
     * the same thing. Refusing them would be refusing the value somebody
     * clearly meant; guessing at anything else would not, so everything
     * outside the list stays a validation error.
     */
    value: z.preprocess((v) => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1 ? true : v === 0 ? false : v;
      if (typeof v !== 'string') return v;
      const word = v.trim().toLowerCase();
      if (['true', 't', 'y', 'yes', '1'].includes(word)) return true;
      if (['false', 'f', 'n', 'no', '0'].includes(word)) return false;
      return v;
    }, z.boolean()),
  }),
]);
export type FilterValue = z.infer<typeof filterValueSchema>;

/** Which arity each operator expects. The gate `validateSpec` checks against. */
export const OP_ARITY: Record<FilterOp, FilterArity> = {
  // The two that genuinely pick from a set.
  in: 'list',
  not_in: 'list',
  // The string comparisons: one value each.
  contains: 'text',
  not_contains: 'text',
  starts_with: 'text',
  ends_with: 'text',
  eq: 'text',
  neq: 'text',
  gt: 'number',
  gte: 'number',
  lt: 'number',
  lte: 'number',
  between: 'numberRange',
  before: 'date',
  after: 'date',
  within: 'macro',
  blank: 'none',
  not_blank: 'none',
};

/**
 * `eq` reads its value as the TYPE asks for it.
 *
 * "is" means one string on a text column, one number on a number, one date on
 * a date and a boolean on a yes/no — the same operator, four value shapes.
 * {@link opArity} resolves it, so nothing downstream has to know the operator
 * and the type to work out what it is holding: the value says.
 */
const DATE_OVERRIDES: Partial<Record<FilterOp, FilterArity>> = {
  eq: 'date',
  neq: 'date',
  gt: 'date',
  gte: 'date',
  lt: 'date',
  lte: 'date',
  between: 'dateRange',
};

/** The arity of one operator ON one column type. */
export function opArity(op: FilterOp, type: FilterValueType): FilterArity {
  if (type === 'boolean' && (op === 'eq' || op === 'neq')) return 'boolean';
  if ((type === 'date' || type === 'datetime') && op in DATE_OVERRIDES) {
    return DATE_OVERRIDES[op] as FilterArity;
  }
  if (type === 'number' && (op === 'eq' || op === 'neq')) return 'number';
  return OP_ARITY[op];
}

export const BLANK_OPS = ['blank', 'not_blank'] as const;

/**
 * Picking exact values — offered by the types a LOOKUP can be annotated into.
 *
 * Every filter written before operators existed is an `in` rule, so a column
 * annotated with its type afterwards must not turn the filters people already
 * saved into specs the guard refuses. Timesheet Date and Timesheet Description
 * were lookups yesterday, which is why `date` and `text` keep it — and it is a
 * real question of both ("these three days", "either of these two codes").
 *
 * `number` and `boolean` do NOT keep it. No dimension is annotated as either,
 * so there is nothing saved to protect, and picking values out of an unbounded
 * numeric set is not a question anybody has.
 */
export const MEMBER_OPS = ['in', 'not_in'] as const;

/**
 * Which operators each type offers, in MENU ORDER.
 *
 * Menu order rather than alphabetical because this list is read top to bottom
 * by somebody choosing, and the blank checks belong at the bottom of that menu
 * — they are the questions nobody opens the menu looking for.
 */
export const OPERATORS_BY_TYPE: Record<FilterValueType, readonly FilterOp[]> = {
  // MEMBERS ONLY, and read as "is" / "is not". A lookup is a key with a label,
  // and the one question asked of it is which members to keep or leave out —
  // so the switch between the two sits in the picker's own panel, above the
  // search box, and the chip carries no operator menu at all.
  lookup: MEMBER_OPS,
  // Picking members first, because a status or a department is usually chosen
  // rather than typed; the substring operators are for the columns where there
  // is nothing to choose from.
  text: [
    ...MEMBER_OPS,
    'eq',
    'neq',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    ...BLANK_OPS,
  ],
  // COMPARISONS ONLY. `is any of` asks somebody to pick values out of an
  // unbounded set, and a blank check on a summed column is a question about
  // the data rather than about the report — both were noise on a menu read
  // top to bottom by somebody who wants "more than 40".
  number: ['gt', 'gte', 'lt', 'lte', 'between'],
  date: ['within', ...MEMBER_OPS, 'eq', 'before', 'after', 'between', ...BLANK_OPS],
  datetime: ['within', ...MEMBER_OPS, 'eq', 'before', 'after', 'between', ...BLANK_OPS],
  // `is` plus the blank checks. Picking "one of Yes" is the same question with
  // more words, so the member operators are left off.
  boolean: ['eq', ...BLANK_OPS],
};

/**
 * What a new filter starts as.
 *
 * The one a reader most often wants, so that adding a filter and typing a value
 * is the whole interaction: members for a lookup, a substring for text, "at
 * least" for a number, a relative window for a date.
 */
export const DEFAULT_OP: Record<FilterValueType, FilterOp> = {
  lookup: 'in',
  text: 'contains',
  number: 'gte',
  date: 'within',
  datetime: 'within',
  boolean: 'eq',
};

/** The words the UI prints, and the words a refusal has to use. ONE copy. */
export const FILTER_OP_LABELS: Record<FilterOp, string> = {
  in: 'is any of',
  not_in: 'is none of',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  eq: 'is',
  neq: 'is not',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  between: 'is between',
  before: 'is before',
  after: 'is after',
  within: 'is within',
  blank: 'is blank',
  not_blank: 'is not blank',
};

/**
 * The words ONE operator reads as on ONE column type.
 *
 * On a lookup the member operators read as "is" / "is not": "Customer is
 * Acme, Globex" is how people say it, and the switch in the picker's panel is
 * a choice between two words, not two phrases. Everywhere else they keep "is
 * any of" / "is none of", because a date column also offers `eq` — and a menu
 * holding two entries that both read "is" is a menu nobody can choose from.
 *
 * The chip, the export's About sheet and a refusal all print through this, so
 * the three cannot name one filter two ways.
 */
export function opLabel(op: FilterOp, type: FilterValueType | null): string {
  if (type === 'lookup' && op === 'in') return 'is';
  if (type === 'lookup' && op === 'not_in') return 'is not';
  return FILTER_OP_LABELS[op];
}

/**
 * Does a chip OFFER this operator on this type? The MENU's answer, which the
 * builder uses to coerce an edit back onto something the chip can show. What a
 * spec may CARRY is wider — see `acceptedOperatorsFor`.
 */
export const allowsOp = (type: FilterValueType, op: FilterOp): boolean =>
  OPERATORS_BY_TYPE[type].includes(op);

/** One condition. */
export const filterRuleSchema = z.object({
  /**
   * Chip identity, so a column may hold several filters — `contains X` AND
   * `does not contain Y` is an ordinary thing to ask, and keying by dimension
   * made it inexpressible.
   */
  id: z.string().min(1).max(64),
  /** A dimension id. */
  field: z.string().min(1).max(200),
  op: z.enum(FILTER_OPS),
  /** Absent for the blank checks, and only for those. */
  value: filterValueSchema.optional(),
  /**
   * On the bar, struck through, not applied.
   *
   * The cheapest way to read a report with and without one condition. It is
   * spec state rather than session state so that the comparison survives a
   * reload — and a saved report that carries one says so on its face.
   */
  paused: z.boolean().default(false),
});
export type FilterRule = z.infer<typeof filterRuleSchema>;

export const FILTER_JOINS = ['and', 'or'] as const;
export type FilterJoin = (typeof FILTER_JOINS)[number];

/** Two or more conditions under ONE connector. */
export const filterGroupSchema = z.object({
  id: z.string().min(1).max(64),
  /**
   * One per group, never mixed.
   *
   * `(A and B or C)` has no reading a bar can render honestly, and the products
   * that allow the mixture all answer the resulting ambiguity with parentheses
   * the reader cannot see. Intercom's rule, and it is the right one.
   */
  join: z.enum(FILTER_JOINS),
  children: z.array(filterRuleSchema).min(2).max(10),
});
export type FilterGroup = z.infer<typeof filterGroupSchema>;

export const filterNodeSchema = z.union([filterGroupSchema, filterRuleSchema]);
export type FilterNode = FilterRule | FilterGroup;

export const isFilterGroup = (node: FilterNode): node is FilterGroup => 'children' in node;

/** Every rule in the tree, groups flattened, in bar order. */
export function filterRules(nodes: readonly FilterNode[]): FilterRule[] {
  return nodes.flatMap((node) => (isFilterGroup(node) ? node.children : [node]));
}

/**
 * What actually reaches the SQL: the paused rules taken out.
 *
 * A group left with one live child becomes that child — a connector with
 * nothing on one side of it is not a group, and emitting `(A or)` is how a
 * pause would otherwise turn into a syntax error rather than a comparison.
 */
export function activeFilters(nodes: readonly FilterNode[]): FilterNode[] {
  const out: FilterNode[] = [];
  for (const node of nodes) {
    if (!isFilterGroup(node)) {
      if (!node.paused) out.push(node);
      continue;
    }
    const live = node.children.filter((child) => !child.paused);
    if (live.length > 1) out.push({ ...node, children: live });
    else if (live.length === 1) out.push(live[0] as FilterRule);
  }
  return out;
}

/** A fresh chip id. Stable ids are the caller's business; this is for new ones. */
export function newFilterId(prefix = 'f'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A canonical form for hashing.
 *
 * Nodes sorted by id, a group's children sorted within it, paused rules gone —
 * so that reordering the bar, or pausing something that was never in the query,
 * is not mistaken for a different question. {@link planKey} is the only caller
 * that matters, and it is the one where being wrong serves another report's
 * rows.
 */
export function canonicalFilters(nodes: readonly FilterNode[]): unknown {
  const rule = (r: FilterRule) => ({ field: r.field, op: r.op, value: r.value ?? null });
  const byField = (a: FilterRule, b: FilterRule) =>
    a.field === b.field
      ? JSON.stringify(rule(a)).localeCompare(JSON.stringify(rule(b)))
      : a.field.localeCompare(b.field);
  return activeFilters(nodes)
    .map((node) =>
      isFilterGroup(node)
        ? { join: node.join, children: [...node.children].sort(byField).map(rule) }
        : { join: 'and' as const, children: [rule(node)] },
    )
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
}

/** The type a dimension's filter speaks. Declared on the dimension; defaults to `lookup`. */
export const DEFAULT_VALUE_TYPE: FilterValueType = 'lookup';
